<template>
  <div class="step-questions">
    <p class="hint">{{ $t('bo.programs.creation.questionsHint') }}</p>

    <section class="block">
      <h4>{{ $t('bo.programs.creation.preSessionQuestions') }}</h4>
      <div class="form-group">
        <textarea
          v-model="questionDraft"
          class="textarea"
          rows="2"
          maxlength="1000"
          :disabled="!canManage || questionLimitReached"
          :placeholder="$t('bo.programs.creation.questionPlaceholder')"
        />
      </div>
      <button
        type="button"
        class="btn-action"
        :disabled="!canManage || !questionDraft.trim() || questionLimitReached || store.isSubmitting"
        @click="addQuestion"
      >
        {{ $t('bo.programs.creation.addQuestion') }}
      </button>
      <ul v-if="preSessionQuestions.length" class="task-list">
        <li v-for="q in preSessionQuestions" :key="q.id">
          <span>{{ q.question_text }}</span>
          <button type="button" class="link-btn danger" :disabled="!canManage" @click="remove(q.id)">
            {{ $t('bo.common.delete') }}
          </button>
        </li>
      </ul>
    </section>

    <section class="block">
      <h4>{{ $t('bo.programs.creation.documentRequests') }}</h4>
      <div class="form-group">
        <input
          v-model="documentDraft"
          type="text"
          class="input"
          maxlength="255"
          :disabled="!canManage"
          :placeholder="$t('bo.programs.creation.documentPlaceholder')"
        />
      </div>
      <label class="checkbox-row">
        <input v-model="documentRequired" type="checkbox" :disabled="!canManage" />
        {{ $t('bo.programs.creation.documentRequired') }}
      </label>
      <button
        type="button"
        class="btn-action"
        :disabled="!canManage || !documentDraft.trim() || store.isSubmitting"
        @click="addDocument"
      >
        {{ $t('bo.programs.creation.addDocument') }}
      </button>
      <ul v-if="documentRequests.length" class="task-list">
        <li v-for="q in documentRequests" :key="q.id">
          <span>{{ q.document_label || q.meta_data?.document_label }} {{ q.is_required ? '*' : '' }}</span>
          <button type="button" class="link-btn danger" :disabled="!canManage" @click="remove(q.id)">
            {{ $t('bo.common.delete') }}
          </button>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useAdminProgramCreationStore } from '@/stores/adminProgramCreation'

const store = useAdminProgramCreationStore()

const questionDraft = ref('')
const documentDraft = ref('')
const documentRequired = ref(false)

const canManage = computed(() => store.programId != null && !store.isArchivedProgram)

const isQuestionTask = (task) => task.target_role === 'mentee' && (task.type ?? 'question') === 'question'
const isDocumentTask = (task) => task.target_role === 'mentee' && task.type === 'request_document'

const preSessionQuestions = computed(() => store.questions.filter(isQuestionTask))
const documentRequests = computed(() => store.questions.filter(isDocumentTask))
const questionLimitReached = computed(() => preSessionQuestions.value.length >= 5)

const getNextOrder = () => {
  if (!store.questions.length) return 0
  return store.questions.reduce((max, item) => Math.max(max, item.order ?? -1), -1) + 1
}

const addQuestion = async () => {
  await store.createQuestion({
    target_role: 'mentee',
    type: 'question',
    question_text: questionDraft.value.trim(),
    order: getNextOrder(),
  })
  questionDraft.value = ''
}

const addDocument = async () => {
  await store.createQuestion({
    target_role: 'mentee',
    type: 'request_document',
    document_label: documentDraft.value.trim(),
    is_required: documentRequired.value,
    order: getNextOrder(),
  })
  documentDraft.value = ''
  documentRequired.value = false
}

const remove = async (id) => {
  await store.deleteQuestion(id)
}

onMounted(async () => {
  if (store.programId) await store.fetchQuestions()
})

watch(
  () => store.programId,
  async (id) => {
    if (id) await store.fetchQuestions()
  }
)
</script>

<style scoped>
.hint {
  margin: 0 0 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.block {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.block h4 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 0.5rem;
}

.input,
.textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-sizing: border-box;
  font-family: inherit;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.btn-action {
  padding: 0.45rem 0.9rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.task-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.task-list li {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
}

.link-btn {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
}

.link-btn.danger {
  color: #dc2626;
}
</style>
